import { useEffect, useRef } from "react";
import { SITE_NAME, SITE_URL } from "../translations";

export default function JsonLD({ data }) {
  const scriptRef = useRef(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    scriptRef.current = script;
    return () => {
      if (scriptRef.current && document.head.contains(scriptRef.current)) {
        document.head.removeChild(scriptRef.current);
      }
    };
  }, [data]);

  return null;
}

// category = { name: "PDF Tools", slug: "pdf-tools" } — optional, enables 3-level breadcrumb
export function buildBreadcrumb(toolTitle, path = "", category = null) {
  const items = [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
  ];
  if (category) {
    items.push({ "@type": "ListItem", "position": 2, "name": category.name, "item": `${SITE_URL}/${category.slug}` });
    items.push({ "@type": "ListItem", "position": 3, "name": toolTitle, "item": `${SITE_URL}${path}` });
  } else {
    items.push({ "@type": "ListItem", "position": 2, "name": toolTitle, "item": `${SITE_URL}${path}` });
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items,
  };
}

export function buildFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };
}

export function buildSoftwareApp() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": SITE_NAME,
    "url": SITE_URL,
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
    "description": "Smart online file tools — 15 free PDF and Excel tools. Fast, secure file conversion with no registration required.",
  };
}

export function buildOrganization() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": `${SITE_URL}/favicon.png`,
    "description": "FileNesto provides 15 free online file tools — fast, free and secure PDF and Excel utilities. Word and image tools coming next.",
    "foundingLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressCountry": "MA" } }
  };
}

export function buildWebSite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
  };
}

export function buildBlogPosting(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "url": `${SITE_URL}/blog/${post.slug}`,
    "datePublished": post.publishedDate,
    "dateModified": post.modifiedDate,
    "author": { "@type": "Organization", "name": SITE_NAME, "url": SITE_URL },
    "publisher": { "@type": "Organization", "name": SITE_NAME, "url": SITE_URL, "logo": { "@type": "ImageObject", "url": `${SITE_URL}/favicon.png` } },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
  };
}
